import { ZonedDateTime } from '@internationalized/date';
import { Calendar, Popover, PopoverContent, PopoverTrigger, TimeInput, RadioGroup, Radio } from '@ds';
import { DEFAULT_DATE_FORMAT_STRING } from '@/constants/date.constants';
import { formatDate, truncateToMinutes } from '@/utils/helpers';

type OrDateTimePickerProps = {
  showOrTypeSwitch?: boolean;
  orDateTime: ZonedDateTime;
  onOrDateTimeChange: (date: ZonedDateTime) => void;
  isCustom: boolean;
  onIsCustomChange: (isCustom: boolean) => void;
};

enum OrType {
  Estimated = 'Estimated',
  Custom = 'Custom',
}

export const OrDateTimePicker = ({
  showOrTypeSwitch,
  orDateTime,
  onOrDateTimeChange,
  isCustom,
  onIsCustomChange,
}: OrDateTimePickerProps) => {
  const getFormattedOrDateTime = () =>
    formatDate(orDateTime.toDate(), DEFAULT_DATE_FORMAT_STRING, { timezone: orDateTime.timeZone });

  const radioValue = isCustom ? OrType.Custom : OrType.Estimated;

  const onRadioChange = (value: string) => onIsCustomChange(value === OrType.Custom);

  if (!showOrTypeSwitch) {
    return <span className="text-m-r text-base-5">OR: {getFormattedOrDateTime()}</span>;
  }

  return (
    <div className="flex items-center">
      <RadioGroup orientation="horizontal" value={radioValue} onValueChange={onRadioChange}>
        <Radio value={OrType.Estimated}>
          <span className="text-base-5 text-m-m">Estimated OR </span>
          {!isCustom && <span className="text-base-5 text-m-r">({getFormattedOrDateTime()})</span>}
        </Radio>
        <Radio value={OrType.Custom}>
          <span className="text-base-5 text-m-m">Custom OR </span>
        </Radio>
      </RadioGroup>
      {isCustom && (
        <Popover placement="bottom">
          <PopoverTrigger>
            <div>
              <span className="text-m-r text-base-5 cursor-pointer">
                (<span className="hover:underline">{getFormattedOrDateTime()}</span>)
              </span>
            </div>
          </PopoverTrigger>
          <PopoverContent className="p-0">
            <Calendar
              aria-label="Custom OR Date"
              value={orDateTime}
              onChange={onOrDateTimeChange}
              bottomContent={
                <div className="flex items-center gap-6 px-4 pt-1 pb-3">
                  <span className="text-l-m">Time</span>
                  <TimeInput
                    size="sm"
                    aria-label="Custom OR Time"
                    hourCycle={24}
                    value={orDateTime}
                    onChange={(zdt) => zdt && onOrDateTimeChange(truncateToMinutes(zdt))}
                  />
                </div>
              }
            />
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
};
